import React, { useState, useEffect } from 'react';
import { useAppContext } from 'context/AppContext';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { useLocation, useHistory } from 'react-router-dom';
import { makeStyles } from 'styles/util';
import { getStorageSite, fetchPriceAffectedBookings, createPriceAdjustment } from 'actions/admin/storageSites';
import { getMessageTemplate } from 'actions/admin/messageTemplates';
import { handleResponse } from 'actions/actionHelpers';
import routes from 'routes';
import { add, startOfMonth } from 'date-fns';
import { getShortDateFormat, formatIsoDate } from 'helpers/DateHelper';
import { convertToNumber } from 'helpers/StringHelper';
import { required, mustBeNumber, mustBeNullOrNumber } from 'form/validation';
import { createEndAdornment } from 'helpers/FormHelper';
import { getStorageTitle } from 'helpers/StorageSiteHelper';
import storageSiteBookingUpdateModes from 'enums/storageSiteBookingUpdateModes';
import messageTypes from 'enums/messageTypes';
import { replaceAll, wrapInParagraphs } from 'helpers/StringHelper';
import queryString from 'query-string';

import Loader from 'common/Loader';
import MarginBox from 'common/MarginBox';
import Amount from 'common/Amount';
import Text from 'common/Text';
import AdminPage from '../../presentational/AdminPage';
import AdminFormWrapper from '../../presentational/AdminFormWrapper';
import AdminFormFields from '../../presentational/AdminFormFields';
import AdminFormButtons from '../../presentational/AdminFormButtons';
import DatePicker from 'form/DatePicker';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { Radios, TextField, Checkboxes, showErrorOnBlur } from 'mui-rff';
import Form from 'form/Form';
import { Link } from 'react-router-dom';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';

const useStyles = makeStyles(({ theme, colors }) => ({
    emailMessage: {
        padding: theme.spacing(0, 2),
        border: `1px solid ${colors.mediumDarkGrey}`,
        backgroundColor: colors.lightGrey,
        borderRadius: '4px'
    },
    bookingCheckboxCell: {
        '&>*': {
            margin: 0
        }
    }
}));

const CreateStorageSitePriceAdjustment = () => {
    const classes = useStyles();
    const { appContext } = useAppContext();
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const params = useParams();

    const { storageSiteId } = params;
    const { bookingIds: bookingIdsString, applyToBookingsOnly: applyToBookingsOnlyString } = queryString.parse(location.search);
    const bookingIds = bookingIdsString?.split(',').map(o => convertToNumber(o));
    const applyToBookingsOnly = applyToBookingsOnlyString === 'true';

    const [storageSite, setStorageSite] = useState(undefined);
    const [priceChangeMessageTemplate, setPriceChangeMessageTemplate] = useState(undefined);
    const [priceAffectedBookings, setPriceAffectedBookings] = useState(undefined);
    const [step, setStep] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const initialValues = {
        date: startOfMonth(add(new Date(), { months: 2 })),
        percentage: null,
        amountDifference: null,
        updateMode: storageSiteBookingUpdateModes.allActiveSubscriptionBookings.key,
        bookings: [],
        sendEmails: true,
        customMessage: null
    };
    const updateModeOptions = [
        { value: storageSiteBookingUpdateModes.noBookings.key, label: 'Keep current prices for all active subscription bookings' },
        { value: storageSiteBookingUpdateModes.someActiveSubscriptionBookings.key, label: 'Choose which active subscription bookings should get updated prices' },
        { value: storageSiteBookingUpdateModes.allActiveSubscriptionBookings.key, label: 'Update prices for all active subscription bookings' }
    ];

    useEffect(() => {
        dispatch(getStorageSite(storageSiteId))
            .then(handleResponse(
                response => {
                    setStorageSite(response.payload);
                    setIsLoading(false);
                },
                () => setIsLoading(false)
            ));
        dispatch(getMessageTemplate(messageTypes.email.key, 'priceChange'))
            .then(handleResponse(
                response => {
                    setPriceChangeMessageTemplate(response.payload);
                }
            ));
    }, []);

    const createCreatePriceAdjustmentRequest = formValues => ({
        date: formatIsoDate(formValues.date, appContext),
        percentage: formValues.percentage
            ? convertToNumber(formValues.percentage) / 100
            : undefined,
        amountDifference: convertToNumber(formValues.amountDifference),
        updateMode: formValues.updateMode,
        applyToBookingsOnly,
        bookingIds: priceAffectedBookings
            ?.map((o, i) => formValues.bookings[i]?.selected ? o.existingBooking.id : undefined)
            .filter(o => o),
        sendEmails: formValues.sendEmails,
        customMessage: formValues.customMessage
    });

    const getPriceChangeInstantForDate = (priceAffectedBooking, date) => priceAffectedBooking.priceChangeInstants.find(o => o.date === formatIsoDate(date, appContext));

    const getPriceChangeMessage = values => {
        let s = priceChangeMessageTemplate;
        s = s?.replace('{customMessage}', values.customMessage ? wrapInParagraphs(values.customMessage) : '');
        s = replaceAll(s, '{storageSiteTitle}', storageSite?.title);
        s = s?.replace('{priceChangeInstants}', '<p>{priceChangeInstants}</p>');
        return s;
    };

    const handleLoadPriceAffectedBookings = (form, formValues) => {
        setIsLoading(true);
        const sanitizedFormValues = createCreatePriceAdjustmentRequest(formValues);
        dispatch(fetchPriceAffectedBookings(storageSiteId, sanitizedFormValues.date, sanitizedFormValues.percentage, sanitizedFormValues.amountDifference))
            .then(handleResponse(
                response => {
                    const pab = response.payload.filter(o => bookingIds?.includes(o.existingBooking.id) ?? true);
                    setPriceAffectedBookings(pab);
                    if(applyToBookingsOnly) {
                        form.change('updateMode', storageSiteBookingUpdateModes.someActiveSubscriptionBookings.key);
                        form.change('bookings', pab.map(() => ({ selected: true })));
                    }
                    setIsLoading(false);
                    setStep(1);
                },
                () => setIsLoading(false)
            ));
    };

    const handleFormSubmit = formValues => {
        setIsSaving(true);
        const request = createCreatePriceAdjustmentRequest(formValues);
        dispatch(createPriceAdjustment(storageSiteId, request))
            .then(handleResponse(
                () => {
                    history.push(routes.admin.storageSite.replace(':storageSiteId', storageSiteId));
                    return { snackbar: { message: 'Storage site was successfully price adjusted.' } };
                },
                () => setIsSaving(false)
            ));
    };

    const minDate = add(new Date(), { days: 30 });

    const title = `Create storage site price adjustment: ${storageSite?.title ?? 'Loading...'}`;

    return (
        <AdminPage title={title}>
            <MarginBox bottom={2}>
                <Typography variant="body1">
                    This form allows you to create a price adjustment for a storage site at a specified date.
                    All storage groups in the storage site will get a new price at this date.
                    If there are active subscription bookings for the storage site that would be affected by the price adjustment,
                    you can choose how to handle these bookings (e.g. update prices for all bookings, choose which bookings get updated prices or keep current prices for all bookings).
                    You can also choose whether to send emails to tenants about the price change and include a custom message in these emails.
                </Typography>
            </MarginBox>
            <AdminFormWrapper isSubmitting={isSaving}>
                <Form
                    initialValues={initialValues}
                    onSubmit={handleFormSubmit}
                >
                    {({ form, handleSubmit, values, invalid }) => {
                        const showBookingCheckboxes = values.updateMode === storageSiteBookingUpdateModes.someActiveSubscriptionBookings.key;
                        const showUpdatedPrice = values.updateMode !== storageSiteBookingUpdateModes.noBookings.key;
                        return (
                            <form onSubmit={handleSubmit}>
                                {
                                    isLoading && <Loader />
                                }
                                {
                                    !isLoading && storageSite &&
                                    (
                                        <>
                                            <AdminFormFields>
                                                <DatePicker
                                                    name="date"
                                                    label="Date for price adjustment"
                                                    formatFunc={getShortDateFormat}
                                                    inputVariant="outlined"
                                                    showError={showErrorOnBlur}
                                                    disabled={step !== 0}
                                                    fieldProps={{ minDate: formatIsoDate(minDate, appContext), validate: required }}
                                                />

                                                <TextField
                                                    name="percentage"
                                                    label="Price adjustment in percent"
                                                    variant="outlined"
                                                    type="text"
                                                    showError={showErrorOnBlur}
                                                    disabled={step !== 0}
                                                    required={!applyToBookingsOnly}
                                                    fieldProps={{ validate: applyToBookingsOnly ? mustBeNullOrNumber : mustBeNumber }}
                                                    InputProps={createEndAdornment('%')}
                                                />
                                                {
                                                    applyToBookingsOnly &&
                                                    (
                                                        <TextField
                                                            name="amountDifference"
                                                            label="Price adjustment as a price difference"
                                                            variant="outlined"
                                                            type="text"
                                                            showError={showErrorOnBlur}
                                                            disabled={step !== 0}
                                                            fieldProps={{ validate: mustBeNullOrNumber }}
                                                            InputProps={createEndAdornment(appContext.currency.label)}
                                                            helperText="Overrides percentage adjustment if both percentage and price difference are provided."
                                                        />
                                                    )
                                                }

                                                {
                                                    step === 1 &&
                                                    (
                                                        <MarginBox top={2}>
                                                            <Typography variant="body1" gutterBottom>
                                                                The change affects active subscription bookings as listed below. How should these bookings be affected?
                                                            </Typography>

                                                            <Radios
                                                                name="updateMode"
                                                                data={updateModeOptions}
                                                                disabled={applyToBookingsOnly}
                                                            />
                                                        </MarginBox>
                                                    )
                                                }

                                                {
                                                    priceAffectedBookings &&
                                                    (
                                                        <Table>
                                                            <TableHead>
                                                                <TableRow>
                                                                    {
                                                                        showBookingCheckboxes && <TableCell/>
                                                                    }
                                                                    <TableCell>
                                                                        Booking ID
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        Tenant
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        Storage
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {
                                                                            showUpdatedPrice && 'Price per month before adjustment'
                                                                        }
                                                                        {
                                                                            !showUpdatedPrice && 'Price per month'
                                                                        }
                                                                    </TableCell>
                                                                    {
                                                                        showUpdatedPrice &&
                                                                        (
                                                                            <TableCell align="right">
                                                                                Price per month after adjustment
                                                                            </TableCell>
                                                                        )
                                                                    }
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {
                                                                    priceAffectedBookings.map((priceAffectedBooking, bookingIndex) => (
                                                                        <TableRow key={priceAffectedBooking.existingBooking.id}>
                                                                            {
                                                                                showBookingCheckboxes &&
                                                                                (
                                                                                    <TableCell className={classes.bookingCheckboxCell}>
                                                                                        <Checkboxes
                                                                                            name={`bookings[${bookingIndex}].selected`}
                                                                                            data={{
                                                                                                value: true
                                                                                            }}
                                                                                        />
                                                                                    </TableCell>
                                                                                )
                                                                            }
                                                                            <TableCell>
                                                                                {priceAffectedBooking.existingBooking.id}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                {priceAffectedBooking.existingBooking.tenantActor.name}
                                                                            </TableCell>

                                                                            <TableCell>
                                                                                {priceAffectedBooking.existingBooking.storage && getStorageTitle(priceAffectedBooking.existingBooking.storage, priceAffectedBooking.existingBooking.storageGroup, appContext)}
                                                                            </TableCell>
                                                                            <TableCell align="right">
                                                                                <Amount value={getPriceChangeInstantForDate(priceAffectedBooking, values.date).previousAmount} currency={priceAffectedBooking.existingBooking.currency}/>
                                                                            </TableCell>
                                                                            {
                                                                                showUpdatedPrice &&
                                                                                (
                                                                                    <TableCell align="right">
                                                                                        {
                                                                                            (values.updateMode === storageSiteBookingUpdateModes.allActiveSubscriptionBookings.key || values.bookings[bookingIndex]?.selected) &&
                                                                                            (
                                                                                                <Amount value={getPriceChangeInstantForDate(priceAffectedBooking, values.date).newAmount} currency={priceAffectedBooking.existingBooking.currency}/>
                                                                                            )
                                                                                        }
                                                                                    </TableCell>
                                                                                )
                                                                            }
                                                                        </TableRow>
                                                                    ))
                                                                }
                                                            </TableBody>
                                                        </Table>
                                                    )
                                                }

                                                {
                                                    step === 1 && showUpdatedPrice &&
                                                    (
                                                        <MarginBox bottom={2}>
                                                            <Checkboxes
                                                                name="sendEmails"
                                                                data={{
                                                                    label: 'Send emails about the change',
                                                                    value: true
                                                                }}
                                                            />
                                                        </MarginBox>
                                                    )
                                                }

                                                {
                                                    step === 1 && showUpdatedPrice && values.sendEmails &&
                                                    (
                                                        <>
                                                            <MarginBox bottom={2}>
                                                                <TextField
                                                                    name="customMessage"
                                                                    label="Custom message in email"
                                                                    type="text"
                                                                    variant="outlined"
                                                                    multiline
                                                                    rows={4}
                                                                    helperText="Additional information or clarifications to be embedded in the email message sent to tenants. A preview of the message is shown below."
                                                                />
                                                            </MarginBox>
                                                            <Box className={classes.emailMessage}>
                                                                <Text
                                                                    html={getPriceChangeMessage(values)}
                                                                    ignoreNewlines
                                                                />
                                                            </Box>
                                                        </>
                                                    )
                                                }

                                            </AdminFormFields>

                                            <AdminFormButtons>
                                                {
                                                    step === 0 &&
                                                    (
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            disabled={invalid}
                                                            onClick={() => handleLoadPriceAffectedBookings(form, values)}
                                                        >
                                                            Load affected bookings
                                                        </Button>
                                                    )
                                                }
                                                {
                                                    step === 1 &&
                                                    (
                                                        <Button
                                                            type="submit"
                                                            variant="contained"
                                                            color="primary"
                                                            disabled={invalid}
                                                        >
                                                            Save
                                                        </Button>
                                                    )
                                                }
                                                <Button
                                                    component={Link}
                                                    to={routes.admin.storageSite.replace(':storageSiteId', storageSiteId)}
                                                    variant="outlined"
                                                    color="primary"
                                                >
                                                    Cancel
                                                </Button>
                                            </AdminFormButtons>
                                        </>
                                    )
                                }
                            </form>
                        );
                    }}
                </Form>
            </AdminFormWrapper>
        </AdminPage>
    );
};

export default CreateStorageSitePriceAdjustment;
